"use client";

import type { GameSearchResult } from "./types";
import { SearchSectionHeader } from "./SearchSectionHeader";
import { GameResultItem } from "./GameResultItem";
import { INITIAL_GAMES_COUNT } from "./constants";

interface GamesResultsSectionProps {
  games: GameSearchResult[];
  query: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onSelect: () => void;
}

/**
 * Розділяємо результати на substring-збіги (запит зустрічається в назві —
 * вважаємо що юзер ввів корректно, просто частково) і fuzzy-збіги (тільки
 * trigram similarity, тобто typo-корекція). Якщо немає substring-ів,
 * показуємо "Did you mean".
 */
function partitionMatches<T extends { name: string }>(
  items: T[],
  query: string,
): { direct: T[]; fuzzy: T[] } {
  const q = query.trim().toLowerCase();
  if (!q) return { direct: items, fuzzy: [] };
  const direct: T[] = [];
  const fuzzy: T[] = [];
  for (const it of items) {
    if (it.name.toLowerCase().includes(q)) direct.push(it);
    else fuzzy.push(it);
  }
  return { direct, fuzzy };
}

export function GamesResultsSection({
  games,
  query,
  isExpanded,
  onToggleExpand,
  onSelect,
}: GamesResultsSectionProps) {
  if (games.length === 0) return null;

  const { direct, fuzzy } = partitionMatches(games, query);

  // Якщо є хоча б один substring-збіг — показуємо стандартний список (повний),
  // fuzzy-збіги залишаємо в хвості, бо вони все одно релевантні.
  // Якщо substring немає взагалі — це означає що юзер мабуть зробив typo,
  // тож показуємо fuzzy під заголовком "Did you mean".
  if (direct.length > 0) {
    const displayed = isExpanded ? games : games.slice(0, INITIAL_GAMES_COUNT);
    const showExpandButton = games.length > INITIAL_GAMES_COUNT;
    const expandCount = games.length - INITIAL_GAMES_COUNT;
    return (
      <div>
        <SearchSectionHeader
          title="Search results"
          showExpandButton={showExpandButton}
          isExpanded={isExpanded}
          expandCount={expandCount}
          onToggleExpand={onToggleExpand}
        />
        <ul className="divide-y divide-[#2a475e]">
          {displayed.map((game, index) => (
            <GameResultItem
              key={`game-${game.appid}`}
              game={game}
              index={index}
              onSelect={onSelect}
            />
          ))}
        </ul>
      </div>
    );
  }

  // Тільки fuzzy → did-you-mean режим
  const displayed = isExpanded ? fuzzy : fuzzy.slice(0, INITIAL_GAMES_COUNT);
  const showExpandButton = fuzzy.length > INITIAL_GAMES_COUNT;
  const expandCount = fuzzy.length - INITIAL_GAMES_COUNT;
  return (
    <div>
      <SearchSectionHeader
        title="Did you mean"
        showExpandButton={showExpandButton}
        isExpanded={isExpanded}
        expandCount={expandCount}
        onToggleExpand={onToggleExpand}
      />
      <ul className="divide-y divide-[#2a475e]">
        {displayed.map((game, index) => (
          <GameResultItem
            key={`game-${game.appid}`}
            game={game}
            index={index}
            onSelect={onSelect}
          />
        ))}
      </ul>
    </div>
  );
}
